import { canBeSentOrDeleted } from '@/services/db/delivery/utils';
import type { Delivery } from '@/services/db/delivery/entity';

/**
 * Row-selection + bulk-action predicates for the deliveries table, ported from
 * the Ember `<cio-deliveries/list>` selection getters. All operate on the raw
 * wire timestamps (a state is "present" when its unix-second field is > 0).
 */

/** A state timestamp is present when it's a positive unix-second value. */
function present(value: number | null | undefined): boolean {
  return typeof value === 'number' && value > 0;
}

/** The retry-schedule timestamp set when an attempted delivery hit a message limit. */
function messageLimitRetry(delivery: Delivery): number | null | undefined {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return (delivery as { meta?: { message_limit_retry?: number | null } }).meta?.message_limit_retry;
}

function isDrafted(delivery: Delivery): boolean {
  return present(delivery.drafted);
}

function isFailed(delivery: Delivery): boolean {
  return present(delivery.failed);
}

function isUndeliverable(delivery: Delivery): boolean {
  return present(delivery.undeliverable);
}

/** Attempted delivery auto-retrying because it hit a message limit (Ember `isRedeliverableAttempted`). */
export function isRedeliverableAttempted(delivery: Delivery): boolean {
  return present(delivery.attempted) && messageLimitRetry(delivery) != null;
}

/** Whether a row can participate in bulk selection (send/retry/delete). */
export function isDeliverySelectable(delivery: Delivery): boolean {
  return canBeSentOrDeleted(delivery) || isRedeliverableAttempted(delivery);
}

/** Any row in the page is failed/undeliverable → the bulk bar shows Retry (else Send). */
export function anyFailedOrUndeliverable(deliveries: readonly Delivery[]): boolean {
  return deliveries.some((delivery) => isFailed(delivery) || isUndeliverable(delivery));
}

/** At least one selected row is retryable (failed/undeliverable). */
export function hasRetryableSelection(selected: readonly Delivery[]): boolean {
  return selected.some((delivery) => isFailed(delivery) || isUndeliverable(delivery));
}

/** Every selected row is a sendable draft (drafted + not yet sent, no pending op). */
export function hasSendableSelection(selected: readonly Delivery[]): boolean {
  return selected.length > 0 && selected.every((delivery) => isDrafted(delivery) && canBeSentOrDeleted(delivery));
}

/** Every selected row is a draft (gates the Delete bulk action). */
export function onlyDraftedSelected(selected: readonly Delivery[]): boolean {
  return selected.length > 0 && selected.every(isDrafted);
}

/** The whole page is drafts (the drafts tab) — surfaces the Delete bulk action. */
export function allDrafted(deliveries: readonly Delivery[]): boolean {
  return deliveries.length > 0 && deliveries.every(isDrafted);
}
